import {Map} from "immutable";
import {IManagerResearchModel} from "../model/manager/IManagerResearchModel";
import {Manager} from "../model/manager/Manager.model";
import {ManagerGroup} from "../model/manager/ManagerGroup";
import {ManagerGroupWithDate} from "../model/manager/ManagerGroupWithDate";
import {ProductSummary} from "../model/product/ProductSummary";
import {Ticker} from "../model/product/Ticker";
import {ResearchSearchItem} from "../model/Search.model";
import {DisplayProductSummary} from "../model/ResearchPageState.model";
import {byName} from "./listUtil";
import {getTranslation} from "./translationUtil";
import {NOT_RATED, NR_RATING} from "./ratingConstants";

export const sortProductsByName = (manager: IManagerResearchModel): IManagerResearchModel => {
    manager.products = manager.products.sort(byName);
    return manager;
};

export const groupProductSummariesByManager = (productSummaries: ProductSummary[]): ManagerGroup[] => {
    const managerAndProductReducer = (managerGroups: ManagerGroup[], productSummary: ProductSummary) => {
        const managerGroup =
            getManagerGroup(
                managerGroups,
                productSummary.manager,
            );
        managerGroup.products.push(productSummary);

        return managerGroups;
    };
    return productSummaries.reduce(managerAndProductReducer, []);
};

export const getManagerGroup = (managerGroups: ManagerGroup[], manager: Manager): ManagerGroup => {
    return managerGroups.find((it) => it.managerBackstopId === manager.managerBackstopId) ||
        addNewManagerGroup(managerGroups, manager.managerBackstopId, manager.name);
};

export const groupProductSummariesByManagerAndDate = (productSummaries: ProductSummary[]): ManagerGroupWithDate[] => {
    const managerWithDateAndProductReducer =
        (managerGroups: ManagerGroupWithDate[], productSummary: ProductSummary) => {
            const managerGroup =
                managerGroups.find(
                    managerWithIdAndDate(
                        productSummary.manager.managerBackstopId,
                        productSummary.currentRatingEffectiveDate),
                )
                || addNewManagerGroupWithDate(
                    managerGroups,
                    productSummary.manager,
                productSummary.currentRatingEffectiveDate,
                );

            managerGroup.products.push(productSummary);

            return managerGroups;
        };
    return productSummaries.reduce(managerWithDateAndProductReducer, []);
};

function managerWithIdAndDate(managerBackstopId: number, date: Date) {
    return (it: ManagerGroupWithDate) => {
        return it.managerBackstopId === managerBackstopId && it.date.getTime() === date.getTime();
    };
}

const addNewManagerGroupWithDate = (managerGroups: ManagerGroupWithDate[], manager: Manager, date: Date) => {
    const managerGroupWithDate = new ManagerGroupWithDate(manager.managerBackstopId, manager.name, date, []);
    managerGroups.push(managerGroupWithDate);
    return managerGroupWithDate;
};

const addNewManagerGroup = (managerGroups: ManagerGroup[], backstopId: number, name: string): ManagerGroup => {
    const newManagerGroup = new ManagerGroup(backstopId, name);
    managerGroups.push(newManagerGroup);
    return newManagerGroup;
};

const productSearchItemsReducer = (searchItems: ResearchSearchItem[],
                                   productSummary: ProductSummary): ResearchSearchItem[] => {
    return [...searchItems, ...getSearchItemsFromTickers(productSummary)];
};

const getSearchItemsForProducts = (productSummaries: ProductSummary[]) => {
    return productSummaries.reduce(productSearchItemsReducer, []);
};

export const getSearchItemsFromProducts = (productSummaries: ProductSummary[]): ResearchSearchItem[] => {
    const productResearchItems = getSearchItemsForProducts(productSummaries);
    const managerResearchItems = getSearchItemsForManagers(productSummaries);
    return [...productResearchItems, ...managerResearchItems];
};

const getResearchItemForProduct = (productSummary: ProductSummary,
                                   productName: string,
                                   tickerId: string | undefined = undefined) => {
    return new ResearchSearchItem(
        productSummary.backstopId,
        productName,
        "PRODUCT",
        `/products/${productSummary.backstopId}`,
        tickerId);
};

const getResearchItemForTicker = (productSummary: ProductSummary) => {
    return (ticker: Ticker) =>
        getResearchItemForProduct(productSummary, `${productSummary.name} (${ticker!.name})`, ticker!.name);
};

const getSearchItemsFromTickers = (productSummary: ProductSummary): ResearchSearchItem[] => {
    return productSummary.tickers.length === 0
        ? [getResearchItemForProduct(productSummary, productSummary.name)]
        : productSummary.tickers
            .sort(byName)
            .map(getResearchItemForTicker(productSummary));
};

function getSearchItemsForManagers(productSummaries: ProductSummary[]): ResearchSearchItem[] {
    return productSummaries.reduce(managerSearchItemReducer, Map()).valueSeq().toArray();
}

const managerSearchItemReducer = (managersResearchItemsByBackstopId: Map<number, ResearchSearchItem>,
                                  productSummary: ProductSummary) => {
    const backstopId = productSummary.manager.managerBackstopId;
    const researchSearchItem = getResearchItemForManager(productSummary);
    return managersResearchItemsByBackstopId.set(backstopId, researchSearchItem);
};

const getResearchItemForManager = (productSummary: ProductSummary) => {
    const backstopId = productSummary.manager.managerBackstopId;
    return new ResearchSearchItem(backstopId,
        productSummary.manager.name,
        "MANAGER",
        `/managers/${backstopId}`,
        undefined,
    );
};

export const fixBlankAndTranslate = (productSummary: ProductSummary): ProductSummary => {
    const blackReplacedProductSummary = replaceBlankByUndefined(productSummary);

    return translateAssetTypeAndProductRating(blackReplacedProductSummary);
};

const replaceBlankByUndefined = (productSummary: ProductSummary): ProductSummary => {
    return {
        ...productSummary,
        region: productSummary.region === "" ? "Undefined" : productSummary.region,
        category: productSummary.category === "" ? "Undefined" : productSummary.category,
        style: productSummary.style === "" ? "Undefined" : productSummary.style,
        approach: productSummary.approach === "" ? "Undefined" : productSummary.approach,
    };
};

const translateAssetTypeAndProductRating = (productSummary: ProductSummary): ProductSummary => {
    return {
        ...productSummary,
        assetType: getTranslation(`asset-class.${productSummary.assetType}`, productSummary.assetType),
        productRating: getTranslation(`research-product-rating.${productSummary.productRating}`, productSummary.productRating),
    };
};

export function byRatingAndName(productSummary1: ProductSummary, productSummary2: ProductSummary)  {
    if(productSummary1.productRating > productSummary2.productRating) return 1;
    if(productSummary1.productRating < productSummary2.productRating) return -1;

    if(productSummary1.name > productSummary2.name) return 1;
    if(productSummary1.name < productSummary2.name) return -1;

    return 0;
}

export function byRatingAndManagerNameAndProductName(productSummary1: ProductSummary, productSummary2: ProductSummary) {
    if(productSummary1.productRating > productSummary2.productRating) return 1;
    if(productSummary1.productRating < productSummary2.productRating) return -1;

    if(productSummary1.manager.name > productSummary2.manager.name) return 1;
    if(productSummary1.manager.name < productSummary2.manager.name) return -1;

    if(productSummary1.name > productSummary2.name) return 1;
    if(productSummary1.name < productSummary2.name) return -1;

    return 0;
}

export const customSearchFn = (row: any, id: string, filterValue: string) => {
    if (filterValue === "") {
        return true;
    }

    return row.getValue(id).toLowerCase().includes(filterValue.toLowerCase())
        || (row.original.tickers && row.original.tickers.some((it:Ticker) =>
            it.name.toLowerCase().includes(filterValue.toLowerCase())));
};

export function escapeRegExp(str: string): string {
    return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

export function highlightMatch(text: string, search: string): {text: string; highlighted: boolean}[] {
    if (!search || !text) return [{text, highlighted: false}];
    const escaped = escapeRegExp(search);
    const regex = new RegExp(`(${escaped})`, "gi");
    const parts = text.split(regex);
    const searchLower = search.trim().toLowerCase();
    return parts.filter(Boolean).map((part) => ({
        text: part,
        highlighted: part.toLowerCase() === searchLower,
    }));
}

function mapAndTranslateOddRating(oddRating: string): string {
    const translated = oddRating.replace(NR_RATING, NOT_RATED);
    return getTranslation(`research-product-rating.${translated}`, translated);
}

export function mapProductForDisplay(productSummary: ProductSummary): DisplayProductSummary {
    return {
        ...productSummary,
        managerName: productSummary.manager.name,
        oddRatingCurrent: mapAndTranslateOddRating(productSummary.oddRating.rating),
    };
}

export function exportResearchToCsv(
    data: Record<string, unknown>[],
    filename: string = "research_table.csv"
): void {
    const headers = [
        "Manager Name", "Strategy Name", "Asset Class",
        "Rating", "ODD Rating", "Approach", "Category", "Region", "Style"
    ];

    const str = (val: unknown): string => String(val || "");
    const escapeCell = (val: unknown) => `"${str(val).replace(/"/g, '""')}"`;
    const mgr = (row: Record<string, unknown>) =>
        (row.manager as {name?: string})?.name || "";

    const rows = data.map((row) => [
        escapeCell(row.managerName || mgr(row)),
        escapeCell(row.name),
        escapeCell(row.assetType),
        escapeCell(row.productRating),
        escapeCell(row.oddRatingCurrent),
        escapeCell(row.approach),
        escapeCell(row.category),
        escapeCell(row.region),
        escapeCell(row.style),
    ].join(","));

    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
}